/**
 * THE branch-matching rule's data and reader, kept free of Firebase imports so the functions
 * codebase's tests can load the client's copy (functions/src/support-module/__tests__/
 * caseBranch.property.test.ts) without the functions typecheck needing the client's node_modules.
 * client/src/types/shared.ts re-exports everything here, so client code keeps importing from there.
 */

/** The canonical default branches, used when there is no branch config document at all. */
export const DEFAULT_BRANCH_SEED = [
  { branchId: 'seaside', name: 'Seaside', code: 'seaside', isActive: true, sortOrder: 1, timezone: 'America/Los_Angeles' },
  { branchId: 'tracy', name: 'Tracy', code: 'tracy', isActive: true, sortOrder: 2, timezone: 'America/Los_Angeles' },
  { branchId: 'reno', name: 'Reno', code: 'reno', isActive: true, sortOrder: 3, timezone: 'America/Los_Angeles' },
] as const;

/** A configured branch as THE branch rule reads it (active or not). */
export interface MatchableBranch {
  branchId: string;
  name: string;
  isActive: boolean;
}

/** The longest branch id the server's case-branch rule accepts (CASE_BRANCH_MAX_LENGTH). */
const MATCHABLE_BRANCH_ID_MAX = 64;

/**
 * EVERY configured branch, active or not, exactly as the server's `caseBranchOptionsFromConfig`
 * reads them (slice E2 delta-1 L1), so the client resolves a stored case branch against the same
 * list the server writes and reports with. A stored id of a DEACTIVATED branch must resolve to that
 * branch (and show Unassigned), never to an active branch named like it; with only the active list
 * it could not know the inactive branch exists.
 *   - no config doc, or no `branches` array -> the default branches (all active);
 *   - otherwise each entry with a string id that trims to 1..64 characters; name trimmed ("" when
 *     not a string); active means exactly `isActive === true`. Order is the config's.
 */
export function branchesForMatching(config: { branches?: unknown } | null | undefined): MatchableBranch[] {
  const raw: unknown = config?.branches;
  if (!Array.isArray(raw)) {
    return DEFAULT_BRANCH_SEED.map((b) => ({ branchId: b.branchId, name: b.name, isActive: true }));
  }
  return raw.flatMap((entry: unknown): MatchableBranch[] => {
    if (!entry || typeof entry !== 'object') return [];
    const { branchId, name, isActive } = entry as { branchId?: unknown; name?: unknown; isActive?: unknown };
    if (typeof branchId !== 'string') return [];
    const id = branchId.trim();
    if (!id || id.length > MATCHABLE_BRANCH_ID_MAX) return [];
    return [{ branchId: id, name: typeof name === 'string' ? name.trim() : '', isActive: isActive === true }];
  });
}
